import React, { useEffect, useRef, useState } from "react";
import {
  getRequestDetail,
  postRequestComment,
  postRequestCommentWithImage,
} from "../../api/agent";
import MessageImage from "./MessageImage";

const STATUS_LABELS = {
  nouveau: "nouveau",
  trie: "trié",
  pret: "prêt",
  recupere: "récupéré",
  en_revue: "en revue",
  livre: "livré",
  rejete: "rejeté",
  en_pause: "en pause",
};

const TYPE_LABELS = {
  bug: "Bug",
  evolution: "Évolution",
  idee: "Idée",
  question: "Question",
};

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

function relativeDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays <= 0) return "aujourd'hui";
  if (diffDays === 1) return "hier";
  if (diffDays < 30) return `il y a ${diffDays} j`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return "il y a 1 mois";
  return `il y a ${diffMonths} mois`;
}

function daysSince(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffDays = Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
  return `depuis ${diffDays} j`;
}

function statusLabel(status) {
  return STATUS_LABELS[status] || status || "";
}

function personaLabel(persona) {
  if (!persona) return "";
  return persona.charAt(0).toUpperCase() + persona.slice(1);
}

function Badge({ children, className = "" }) {
  return (
    <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${className}`}>
      {children}
    </span>
  );
}

export default function RequestDetail({ token, requestId, isTeam = false, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [commentText, setCommentText] = useState("");
  const [pendingImage, setPendingImage] = useState(null);
  const pendingImageRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  function discardPendingImage() {
    if (pendingImageRef.current?.url) {
      URL.revokeObjectURL(pendingImageRef.current.url);
    }
    pendingImageRef.current = null;
    setPendingImage(null);
  }

  function stagePendingImage(file) {
    if (pendingImageRef.current?.url) {
      URL.revokeObjectURL(pendingImageRef.current.url);
    }
    const nextImage = { file, url: URL.createObjectURL(file) };
    pendingImageRef.current = nextImage;
    setPendingImage(nextImage);
  }

  useEffect(() => {
    discardPendingImage();
  }, [requestId]);

  useEffect(() => () => {
    if (pendingImageRef.current?.url) {
      URL.revokeObjectURL(pendingImageRef.current.url);
    }
    pendingImageRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    getRequestDetail(token, requestId)
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        setLoadError("Impossible de charger cette demande.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, requestId]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!commentText.trim() || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const updated = pendingImage
        ? await postRequestCommentWithImage(
            token,
            requestId,
            commentText,
            pendingImage.file,
          )
        : await postRequestComment(token, requestId, commentText);
      setDetail(updated);
      setCommentText("");
      discardPendingImage();
      onUpdated?.(updated);
    } catch (error) {
      console.error(error);
      setSubmitError("Le commentaire n'a pas pu être envoyé.");
    } finally {
      setSubmitting(false);
    }
  }

  const history = detail?.status_history || [];
  const comments = detail?.comments || [];

  return (
    <div
      className="request-detail-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="request-detail-panel max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="request-detail-header sticky top-0 z-10 flex items-start justify-between gap-4 border-b bg-white px-5 py-4">
          <div className="min-w-0">
            <h2 className="request-detail-title text-lg font-semibold text-gray-900">
              {detail?.title || "Demande"}
            </h2>
            {detail && (
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge className="bg-blue-50 text-blue-700">
                  {TYPE_LABELS[detail.type] || detail.type}
                </Badge>
                {isTeam && detail.priority && (
                  <Badge className="bg-orange-50 text-orange-700">{detail.priority}</Badge>
                )}
                <Badge className="bg-gray-100 text-gray-700">
                  {statusLabel(detail.status)}
                </Badge>
                <Badge className="bg-gray-50 text-gray-600">
                  {daysSince(detail.status_since)}
                </Badge>
              </div>
            )}
            {isTeam && detail?.priority && detail.priority_by && (
              <p className="priority-attribution mt-2 text-xs text-gray-500">
                Priorité posée par {personaLabel(detail.priority_by)}
                {detail.priority_at ? ` le ${formatDate(detail.priority_at)}` : ""}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <div className="space-y-6 px-5 py-5">
          {loading && <p className="text-sm text-gray-500">Chargement…</p>}
          {loadError && <p className="text-sm text-red-600">{loadError}</p>}

          {detail && (
            <>
              <section className="space-y-3">
                {detail.description && (
                  <p className="whitespace-pre-wrap text-sm leading-6 text-gray-800">
                    {detail.description}
                  </p>
                )}
                {detail.public_note && (
                  <div className="rounded-md border border-orange-100 bg-orange-50 px-3 py-2 text-sm text-orange-900">
                    {detail.public_note}
                  </div>
                )}
              </section>

              <section>
                <h3 className="mb-3 text-sm font-semibold text-gray-800">Timeline statuts</h3>
                <ol className="space-y-2 border-l border-gray-200 pl-4">
                  {history.length === 0 && (
                    <li className="relative text-sm text-gray-700">
                      <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-gray-300" />
                      créée le {formatDate(detail.created_at)}
                    </li>
                  )}
                  {history.map((change, index) => (
                    <li key={`${change.at}-${index}`} className="relative text-sm text-gray-700">
                      <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-orange-500" />
                      <span className="font-medium">
                        {statusLabel(change.from_status)} → {statusLabel(change.to_status)}
                      </span>
                      <span className="text-gray-500">
                        {" "}· {formatDate(change.at)} · {change.by || "unknown"}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>

              <section>
                <h3 className="mb-3 text-sm font-semibold text-gray-800">Commentaires</h3>
                {comments.length === 0 && (
                  <p className="mb-3 text-sm text-gray-500">Aucun commentaire pour l'instant.</p>
                )}
                {comments.length > 0 && (
                  <ul className="mb-4 space-y-3">
                    {comments.map((comment) => (
                      <li key={comment.id} className="rounded-md border bg-gray-50 px-3 py-2">
                        <div className="mb-1 text-xs text-gray-500">
                          <span className="font-semibold text-gray-800">{comment.author}</span>
                          {" "}· {relativeDate(comment.at)}
                        </div>
                        <p className="whitespace-pre-wrap text-sm text-gray-800">{comment.text}</p>
                        {comment.image_path && (
                          <MessageImage
                            imageUrl={
                              `/api/agent/my-requests/${requestId}` +
                              `/comments/${comment.id}/image`
                            }
                            token={token}
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                <form onSubmit={handleSubmit} className="space-y-2">
                  {pendingImage && (
                    <div className="flex items-center gap-2">
                      <img
                        src={pendingImage.url}
                        alt="à envoyer"
                        className="h-16 max-w-full rounded border"
                      />
                      <button
                        type="button"
                        onClick={discardPendingImage}
                        className="text-xs text-gray-500 hover:text-red-600"
                      >
                        ✕ retirer
                      </button>
                    </div>
                  )}
                  <textarea
                    value={commentText}
                    onChange={(event) => setCommentText(event.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="Ajouter un commentaire…"
                  />
                  {submitError && <p className="text-sm text-red-600">{submitError}</p>}
                  <div className="flex justify-between gap-2">
                    <label
                      className="cursor-pointer rounded-md border px-3 py-2 text-gray-600 hover:bg-gray-50"
                      title="Joindre une image"
                    >
                      📎
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) {
                            stagePendingImage(file);
                          }
                          event.target.value = "";
                        }}
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={submitting || !commentText.trim()}
                      className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {submitting ? "Envoi…" : "Commenter"}
                    </button>
                  </div>
                </form>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
